import {Box, Flex} from '@indoqa/style-system'
import {IStyle} from 'fela'
import * as React from 'react'
import {useFela} from 'react-fela'
import {useParams} from 'react-router-dom'
import {Theme} from '../../app/theme'
import {Button, Type} from '../../commons/components/button/Button'
import {ReaderFooterPanel} from '../../commons/components/footer/ReaderFooterPanel'
import {DigitalHelmet} from '../../commons/components/helmet/DigitalHelmet'
import {MainContainerPanel} from '../../commons/components/main/MainContainerPanel'
import {ReaderLayout} from '../../commons/layouts/reader-layout/ReaderLayout'
import {digitalArfbrLoginPage} from '../../digital/store/digital.paths'
import {ARHeaderPanel} from '../components/header/ARHeaderPanel'
import {ArLink} from '../components/links/ArLink'
import {arPathAccountVerification} from '../store/ar.paths'

interface LocationParams {
  verificationId: string
}

type PageState = 'loading' | 'invalid' | 'verified' | 'success'

const TEST_VERIFICATION_ID = '123'

const PASSWORD_MIN_LENGTH = 10
const PASSWORD_MAX_LENGTH = 50

function validatePassword(password: string): string[] {
  const errors: string[] = []
  if (password.length < PASSWORD_MIN_LENGTH) {
    errors.push(`Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`)
  }
  if (password.length > PASSWORD_MAX_LENGTH) {
    errors.push(`Das Passwort darf maximal ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`)
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Das Passwort muss mindestens einen Großbuchstaben enthalten.')
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Das Passwort muss mindestens eine Ziffer enthalten.')
  }
  return errors
}

const EyeOpenIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const EyeClosedIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
)

export const AccountVerificationPage = () => {
  const {css, theme} = useFela<Theme>()
  const {verificationId} = useParams<LocationParams>()

  const [pageState, setPageState] = React.useState<PageState>('loading')
  const [pwd1, setPwd1] = React.useState('')
  const [pwd2, setPwd2] = React.useState('')
  const [showPwd1, setShowPwd1] = React.useState(false)
  const [showPwd2, setShowPwd2] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState(false)

  const isTestMode = verificationId === TEST_VERIFICATION_ID

  React.useEffect(() => {
    if (isTestMode) {
      setPageState('verified')
      return
    }
    fetch(`/api/v1/auth/verify-registration?token=${verificationId}`)
      .then((response) => {
        if (response.ok) {
          setPageState('verified')
        } else {
          setPageState('invalid')
        }
      })
      .catch(() => {
        setPageState('invalid')
      })
  }, [verificationId, isTestMode])

  const passwordErrors = validatePassword(pwd1)
  const passwordsMatch = pwd1 === pwd2

  const handleSubmit = React.useCallback(() => {
    setSubmitted(true)
    setSubmitError(false)

    if (passwordErrors.length > 0 || !passwordsMatch) {
      return
    }

    setSubmitting(true)

    if (isTestMode) {
      setPageState('success')
      setSubmitting(false)
      return
    }

    fetch(`/api/v1/auth/complete-registration`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({token: verificationId, password: pwd1}),
    })
      .then((response) => {
        if (response.ok) {
          setPageState('success')
        } else {
          setSubmitError(true)
        }
      })
      .catch(() => {
        setSubmitError(true)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }, [verificationId, isTestMode, pwd1, passwordErrors.length, passwordsMatch])

  const formStyle: IStyle = {
    width: '100%',
    maxWidth: 500,
  }
  const inputStyle: IStyle = {
    width: '100%',
    boxSizing: 'border-box',
    border: theme.borders.border1,
    padding: theme.spacing.space2,
    paddingRight: theme.spacing.space8,
    fontSize: '100%',
  }
  const inputWrapperStyle: IStyle = {
    position: 'relative',
    width: '100%',
  }
  const toggleButtonStyle: IStyle = {
    position: 'absolute',
    top: '50%',
    right: theme.spacing.space2,
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: theme.colors.text,
  }
  const errorStyle: IStyle = {
    color: theme.colors.accent,
    fontSize: '85%',
    marginTop: theme.spacing.space1,
  }
  const labelStyle: IStyle = {
    display: 'block',
    marginBottom: theme.spacing.space1,
    fontWeight: 'bold',
  }
  const fieldStyle: IStyle = {
    marginBottom: theme.spacing.space4,
  }

  const arHeader = <ARHeaderPanel />
  const arFooter = <ReaderFooterPanel />
  const canonical = arPathAccountVerification(verificationId)

  const contactInfo = (
    <Box pt={4}>
      <p>
        Wir helfen dir gerne weiter. Kontaktiere unser Serviceteam unter{' '}
        <ArLink name="serviceteam@oegbverlag.at" link="mailto:serviceteam@oegbverlag.at" />
      </p>
    </Box>
  )

  if (pageState === 'loading') {
    return (
      <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
        <DigitalHelmet title="Verifizierung" canonical={canonical} />
        <MainContainerPanel>
          <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
            <p>Wird geladen...</p>
          </Flex>
        </MainContainerPanel>
      </ReaderLayout>
    )
  }

  if (pageState === 'invalid') {
    return (
      <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
        <DigitalHelmet title="Verifizierung" canonical={canonical} />
        <MainContainerPanel>
          <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
            <h1>Verifizierung nicht möglich</h1>
            <Box pt={4} pb={8}>
              <p>
                Der Verifizierungslink ist leider nicht mehr gültig oder wurde nicht gefunden. Bitte fordere einen neuen
                Link an.
              </p>
              {contactInfo}
            </Box>
          </Flex>
        </MainContainerPanel>
      </ReaderLayout>
    )
  }

  if (pageState === 'success') {
    return (
      <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
        <DigitalHelmet title="Passwort erfolgreich gesetzt" canonical={canonical} />
        <MainContainerPanel>
          <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
            <Flex direction="row" style={{alignItems: 'center'}}>
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{marginRight: 10, flexShrink: 0}}
              >
                <circle cx="12" cy="12" r="12" fill="#4CAF50" />
                <path d="M9.5 15.6L6.4 12.5L5.3 13.6L9.5 17.8L19.5 7.8L18.4 6.7L9.5 15.6Z" fill="white" />
              </svg>
              <h1 style={{margin: 0}}>Dein Passwort wurde erfolgreich gesetzt</h1>
            </Flex>
            <Box pt={4} pb={8}>
              <p>
                <strong>Alles bereit! Deine Registrierung ist abgeschlossen.</strong>
              </p>
              <p>
                Dein Testzugang für <b>„Arbeitsrecht für Betriebsräte“</b> ist jetzt aktiv. Du kannst dich sofort
                anmelden und alle Inhalte nutzen.
              </p>
              <p>Viel Erfolg beim Entdecken von „Arbeitsrecht für Betriebsräte“!</p>
              <p>
                <b>Dein ÖGB Verlag</b>
              </p>
              <Box pt={4}>
                <ArLink name="Jetzt einloggen" link={digitalArfbrLoginPage()} />
              </Box>
            </Box>
            {contactInfo}
          </Flex>
        </MainContainerPanel>
      </ReaderLayout>
    )
  }

  return (
    <ReaderLayout header={arHeader} footer={arFooter} enableScrollTop>
      <DigitalHelmet title="Registrierung abschließen" canonical={canonical} />
      <MainContainerPanel>
        <Flex direction="column" fullWidth pt={12} pb={12} maxWidth={800} style={{margin: '0 auto'}}>
          <h1>Registrierung erfolgreich abgeschlossen &#10003;</h1>
          <Box pb={4}>
            <p>Vergib jetzt dein Passwort:</p>
          </Box>

          <div className={css(formStyle)}>
            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>Passwort</label>
              <div className={css(inputWrapperStyle)}>
                <input
                  className={css(inputStyle)}
                  type={showPwd1 ? 'text' : 'password'}
                  value={pwd1}
                  onChange={(e) => setPwd1(e.target.value)}
                />
                <button
                  type="button"
                  className={css(toggleButtonStyle)}
                  aria-label={showPwd1 ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  aria-pressed={showPwd1}
                  onClick={() => setShowPwd1((v) => !v)}
                >
                  {showPwd1 ? <EyeOpenIcon /> : <EyeClosedIcon />}
                </button>
              </div>
              {!submitted && (
                <Box pt={1} style={{fontSize: '85%'}}>
                  <ul>
                    <li>mindestens 10 Zeichen</li>
                    <li>mindestens ein Großbuchstabe</li>
                    <li>mindestens eine Ziffer</li>
                  </ul>
                </Box>
              )}
              {submitted &&
                passwordErrors.map((error) => (
                  <div key={error} className={css(errorStyle)}>
                    {error}
                  </div>
                ))}
            </div>

            <div className={css(fieldStyle)}>
              <label className={css(labelStyle)}>Passwort bestätigen</label>
              <div className={css(inputWrapperStyle)}>
                <input
                  className={css(inputStyle)}
                  type={showPwd2 ? 'text' : 'password'}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                />
                <button
                  type="button"
                  className={css(toggleButtonStyle)}
                  aria-label={showPwd2 ? 'Passwort verbergen' : 'Passwort anzeigen'}
                  aria-pressed={showPwd2}
                  onClick={() => setShowPwd2((v) => !v)}
                >
                  {showPwd2 ? <EyeOpenIcon /> : <EyeClosedIcon />}
                </button>
              </div>
              {submitted && !passwordsMatch && (
                <div className={css(errorStyle)}>Die Passwörter stimmen nicht überein.</div>
              )}
            </div>

            <Box pt={2} pb={4}>
              <Button
                type={Type.CTA}
                text={submitting ? 'Wird gesendet...' : 'Weiter'}
                linkTo={handleSubmit}
                disabled={submitting}
              />
            </Box>

            {submitError && (
              <Box pb={4}>
                <div className={css(errorStyle)}>
                  Beim Setzen des Passworts ist ein Fehler aufgetreten. Bitte versuche es später erneut.
                </div>
              </Box>
            )}
          </div>

          {contactInfo}
        </Flex>
      </MainContainerPanel>
    </ReaderLayout>
  )
}
